/**
 * @page /portal/vorschlaege (MA-Portal: Prozess-Vorschlaege)
 * @description QM Modul 5 (#920) — App-Alternative zum Telegram-Bot.
 *   MA reicht Verbesserungs-Vorschlag per Text ein, sieht eigene Vorschlaege + Status.
 * @api POST /api/portal/prozess-feedback, GET /api/portal/prozess-feedback/meine
 * @roles portal_mitarbeiter
 * @i18n qmFeedback.portal.*
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowLeft, Lightbulb, Send, CheckCircle2, Clock, AlertCircle, ThumbsUp, ThumbsDown, HelpCircle, Loader2 } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

const STATUS_META = {
  offen:        { color: '#3B82F6', bg: '#EFF6FF', Icon: Clock,         labelKey: 'qmFeedback.status.offen',        labelDef: 'Offen' },
  in_pruefung:  { color: '#F59E0B', bg: '#FFFBEB', Icon: AlertCircle,   labelKey: 'qmFeedback.status.in_pruefung',  labelDef: 'In Pruefung' },
  rueckfrage:   { color: '#A855F7', bg: '#FAF5FF', Icon: HelpCircle,    labelKey: 'qmFeedback.status.rueckfrage',   labelDef: 'Rueckfrage' },
  angenommen:   { color: '#10B981', bg: '#ECFDF5', Icon: ThumbsUp,      labelKey: 'qmFeedback.status.angenommen',   labelDef: 'Angenommen' },
  abgelehnt:    { color: '#EF4444', bg: '#FEF2F2', Icon: ThumbsDown,    labelKey: 'qmFeedback.status.abgelehnt',    labelDef: 'Abgelehnt' },
};

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function PortalVorschlaege() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState('');
  const [error, setError] = useState('');

  const load = () => {
    fetch(`${API}/api/portal/prozess-feedback/meine`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(d => { setItems(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSubmitting(true); setError(''); setSubmitMsg('');
    try {
      const r = await fetch(`${API}/api/portal/prozess-feedback`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ vorschlag_text: text.trim() }),
      });
      if (r.ok) {
        setSubmitMsg(t('qmFeedback.portal.submitted', 'Danke! Dein Vorschlag wurde uebermittelt.'));
        setText('');
        load();
      } else {
        const d = await r.json().catch(() => ({}));
        setError(d.error || t('qmFeedback.portal.error', 'Fehler beim Senden'));
      }
    } catch { setError(t('qmFeedback.portal.error_network', 'Netzwerk-Fehler')); }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-app, #F8F9FC)' }}>
      <div className="px-4 py-4 flex items-center gap-3 border-b" style={{ borderColor: 'var(--border-card, #E5E7EB)', background: 'var(--bg-card, #FFFFFF)' }}>
        <button onClick={() => navigate('/portal')} className="p-1.5 rounded-lg hover:bg-[var(--bg-inset,#F8F9FC)]" aria-label="Zurück">
          <ArrowLeft className="w-5 h-5" strokeWidth={1.5} style={{ color: 'var(--text-primary, #1A1A2E)' }} />
        </button>
        <div className="flex items-center gap-2">
          <Lightbulb className="w-5 h-5" strokeWidth={1.5} style={{ color: '#338edc' }} />
          <h1 className="text-base font-semibold" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
            {t('qmFeedback.portal.title', 'Verbesserungs-Vorschlag')}
          </h1>
        </div>
      </div>

      <div className="p-4 max-w-3xl mx-auto space-y-4">
        {/* Form */}
        <form onSubmit={submit} className="rounded-2xl p-5" style={{ background: 'var(--bg-card, #FFFFFF)', border: '1px solid var(--border-card, #E5E7EB)' }}>
          <p className="text-sm mb-3" style={{ color: 'var(--text-secondary, #4B5563)' }}>
            {t('qmFeedback.portal.intro', 'Was koennte besser laufen? Beschreibe deinen Vorschlag — die Zuordnung zum richtigen Prozess uebernimmt unser System.')}
          </p>
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder={t('qmFeedback.portal.placeholder', 'z.B. „Beim Picken in Halle B koennte man die Etiketten am Tisch aufkleben statt am Karton — wuerde Zeit sparen…"')}
            rows={6}
            maxLength={4000}
            className="w-full px-3 py-2 rounded-[10px] border text-sm resize-none focus:outline-none focus:border-[#338edc]"
            style={{ borderColor: 'var(--border-input, #E5E7EB)', background: 'var(--bg-input, #FFFFFF)', color: 'var(--text-primary, #1A1A2E)' }}
            disabled={submitting}
          />
          <div className="flex items-center justify-between mt-2">
            <span className="text-[11px]" style={{ color: 'var(--text-tertiary, #6B7280)' }}>{text.length} / 4000</span>
            <button type="submit" disabled={submitting || !text.trim()}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white text-sm font-semibold disabled:opacity-50">
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              {t('qmFeedback.portal.send', 'Senden')}
            </button>
          </div>
          {submitMsg && (
            <div className="mt-3 px-3 py-2 rounded-[10px] text-xs flex items-center gap-2"
                 style={{ background: '#10B98118', color: '#047857' }}>
              <CheckCircle2 size={14} /> {submitMsg}
            </div>
          )}
          {error && (
            <div className="mt-3 px-3 py-2 rounded-[10px] text-xs"
                 style={{ background: '#EF44440D', color: '#B91C1C', border: '1px solid #EF444433' }}>
              {error}
            </div>
          )}
        </form>

        {/* Eigene Vorschlaege */}
        <div>
          <h2 className="text-sm font-semibold mb-2 px-1" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
            {t('qmFeedback.portal.meine', 'Meine Vorschlaege')}
          </h2>
          {loading ? (
            <div className="text-center py-8 text-sm" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
              <Loader2 size={16} className="inline animate-spin mr-2" />
              {t('common.loading', 'Lade…')}
            </div>
          ) : items.length === 0 ? (
            <div className="rounded-2xl p-6 text-center" style={{ background: 'var(--bg-card, #FFFFFF)', border: '1px solid var(--border-card, #E5E7EB)' }}>
              <Lightbulb className="w-8 h-8 mx-auto mb-2 opacity-40" strokeWidth={1.5} style={{ color: 'var(--text-tertiary, #6B7280)' }} />
              <p className="text-sm" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
                {t('qmFeedback.portal.empty', 'Noch keine Vorschlaege eingereicht.')}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {items.map(it => {
                const meta = STATUS_META[it.status] || STATUS_META.offen;
                const Icon = meta.Icon;
                return (
                  <div key={it.id} className="rounded-2xl p-4"
                       style={{ background: 'var(--bg-card, #FFFFFF)', border: '1px solid var(--border-card, #E5E7EB)' }}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium line-clamp-2" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
                          {it.vorschlag_zusammenfassung || '—'}
                        </div>
                        {it.prozess_titel && (
                          <div className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
                            {it.prozess_titel}{it.schritt_nummer ? ` · Schritt ${it.schritt_nummer}` : ''}
                          </div>
                        )}
                      </div>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap"
                            style={{ background: meta.bg, color: meta.color }}>
                        <Icon size={11} />
                        {t(meta.labelKey, meta.labelDef)}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-[11px]" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
                      <span>{fmtDate(it.eingereicht_am)}</span>
                      {it.bearbeitet_am && <span>·  {t('qmFeedback.portal.bearbeitet_am', 'bearbeitet')}: {fmtDate(it.bearbeitet_am)}</span>}
                    </div>
                    {it.resultat_kommentar && (
                      <div className="mt-2 px-3 py-2 rounded-[10px] text-xs"
                           style={{ background: 'var(--bg-inset, #F8F9FC)', color: 'var(--text-primary, #1A1A2E)', border: '1px solid var(--border-card, #E5E7EB)' }}>
                        <span className="font-semibold">{t('qmFeedback.portal.kommentar', 'Antwort')}:</span> {it.resultat_kommentar}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
